/**
 * Normalize an axios/FastAPI error into a plain string.
 *
 * Why this exists: FastAPI's `detail` is only a string for the errors we
 * raise ourselves (`HTTPException(detail="...")`). Request-validation
 * failures (422) return `detail` as an ARRAY of error objects:
 *
 *   [{ type, loc: [...], msg, input, url }]
 *
 * Passing that array straight into `toast.error(...)` hands a non-element
 * object to React as a child. React throws "Objects are not valid as a
 * React child" during the Toaster's render, and because the throw happens
 * in the render phase React unmounts the whole root — the entire app goes
 * blank white. Always funnel API errors through here before displaying.
 */
export function apiErrorMessage(err, fallback = 'Something went wrong') {
  const detail = err?.response?.data?.detail;
  return detailToMessage(detail) || err?.message || fallback;
}

/**
 * The server's message as text, or ``undefined`` when there is none — the
 * drop-in for ``err.response?.data?.detail || 'fallback'``. That pattern sat
 * in some 340 places and passed an object ``detail`` (a 422 list, or a
 * ``{code, message}`` refusal) straight to a toast or to state, which blanks
 * the whole app (see above). ``apiDetail(err) || 'fallback'`` keeps every
 * call site's own fallback.
 */
export function apiDetail(err) {
  return detailToMessage(err?.response?.data?.detail) || undefined;
}

/**
 * PERM-14 — an approval above the person's limit is not a failure: their
 * approval is recorded and the item waits for someone whose limit covers it.
 * Returns the sentence to show (as a notice, not an error), or null.
 */
export function approvalRoutedMessage(err) {
  const detail = err?.response?.data?.detail;
  // ABOVE_APPROVAL_LIMIT: the approver's limit stopped it (PERM-14 wording).
  // APPROVAL_ROUTED: the approval engine routed it (another step, maker-
  // checker, scope, no policy) — APR-3.
  if (detail && typeof detail === 'object'
      && (detail.code === 'ABOVE_APPROVAL_LIMIT' || detail.code === 'APPROVAL_ROUTED')) {
    return detail.message || 'Sent for approval — it is waiting for someone else to approve it.';
  }
  return null;
}

/** Flatten a FastAPI `detail` payload of any shape into a string. */
export function detailToMessage(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail.trim();
  if (typeof detail === 'number' || typeof detail === 'boolean') return String(detail);

  // 422 validation errors: array of { loc, msg, ... }
  if (Array.isArray(detail)) {
    const parts = detail.map(formatValidationError).filter(Boolean);
    // De-dupe — Pydantic often reports the same message per array element.
    return [...new Set(parts)].join('; ');
  }

  if (typeof detail === 'object') {
    // Some handlers return { message: "..." } or a single error object.
    if (typeof detail.message === 'string') return detail.message.trim();
    return formatValidationError(detail);
  }

  return '';
}

/**
 * Render one validation error as "field: message", skipping the leading
 * "body"/"query" segment and numeric list indices so the user sees the
 * field they actually touched.
 */
function formatValidationError(e) {
  if (typeof e === 'string') return e.trim();
  if (!e || typeof e !== 'object') return '';

  const msg = typeof e.msg === 'string' ? e.msg.trim() : '';
  const loc = Array.isArray(e.loc)
    ? e.loc.filter((p) => typeof p === 'string' && !['body', 'query', 'path', 'header'].includes(p))
    : [];

  if (!msg) return loc.length ? loc.join('.') : '';
  return loc.length ? `${loc.join('.')}: ${msg}` : msg;
}
